import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Receipt as OrdersIcon,
} from '@mui/icons-material';
import {
  useProducts,
  useSales,
  useCreateReturnInvoice,
  useReturnInvoices,
  useDeleteReturnInvoice,
  fetchReturnInvoiceById,
} from '../services/api';
import { printReturnInvoices } from '../utils/print';

const buildReturnItemPayload = (product, quantity) => ({
  productId: product.id,
  quantity,
  unitPrice: parseFloat(product.price) || 0,
  cost: parseFloat(product.cost) || 0,
  discount: parseFloat(product.discount) || 0,
  returnReason: '',
  productName: product.name || '',
  productNameArabic: product.nameArabic || '',
  productImageUrl: product.imageUrl || '',
  categoryName: product.category?.name || '',
  categoryNameArabic: product.category?.nameArabic || '',
  trademarkName: product.trademark?.name || '',
  trademarkNameArabic: product.trademark?.nameArabic || '',
});

const ReturnInvoicePage = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();

  const [productPage, setProductPage] = useState(0);
  const [productRowsPerPage, setProductRowsPerPage] = useState(10);
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceRowsPerPage, setInvoiceRowsPerPage] = useState(10);

  const [selection, setSelection] = useState({});
  const [notes, setNotes] = useState('');
  const [totalOverride, setTotalOverride] = useState('');
  const [totalManuallyEdited, setTotalManuallyEdited] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [printingId, setPrintingId] = useState(null);
  const [productSearch, setProductSearch] = useState('');

  const isProductSearching = !!productSearch.trim();

  const { data: productData, isLoading: productsLoading } = useProducts({
    page: isProductSearching ? 1 : productPage + 1,
    per_page: isProductSearching ? 1000 : productRowsPerPage,
  });
  const { data: sales = [] } = useSales();
  const {
    data: invoiceData,
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useReturnInvoices({
    page: invoicePage + 1,
    per_page: invoiceRowsPerPage,
    sales_id: saleId,
  });

  const createReturnInvoice = useCreateReturnInvoice();
  const deleteReturnInvoice = useDeleteReturnInvoice();

  const products = productData?.products || [];
  const productMeta = productData?.meta || {};

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) => {
      const name = (product.name || '').toLowerCase();
      const nameArabic = (product.nameArabic || '').toLowerCase();
      return name.includes(query) || nameArabic.includes(query);
    });
  }, [products, productSearch]);

  const displayProducts = useMemo(() => {
    if (!isProductSearching) return products;
    const start = productPage * productRowsPerPage;
    return filteredProducts.slice(start, start + productRowsPerPage);
  }, [isProductSearching, products, filteredProducts, productPage, productRowsPerPage]);

  const productTableCount = isProductSearching
    ? filteredProducts.length
    : productMeta.total ?? 0;
  const invoices = invoiceData?.invoices || [];
  const invoiceMeta = invoiceData?.meta || {};
  const sale = sales.find((s) => String(s.id) === String(saleId));

  const lineItems = useMemo(() => {
    return Object.values(selection)
      .filter((entry) => entry.checked && entry.product)
      .map((entry) => {
        const quantity = parseInt(entry.returnedPieces, 10) || 0;
        const unitPrice = parseFloat(entry.product.price) || 0;
        return {
          product: entry.product,
          quantity,
          lineTotal: unitPrice * quantity,
        };
      });
  }, [selection]);

  const calculatedTotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + item.lineTotal, 0),
    [lineItems]
  );

  const displayTotal = totalManuallyEdited
    ? parseFloat(totalOverride) || 0
    : calculatedTotal;

  const selectedCount = lineItems.filter((item) => item.quantity > 0).length;

  const handleToggleProduct = (product) => {
    setSelection((prev) => {
      const current = prev[product.id];
      const nextChecked = !current?.checked;
      if (!nextChecked) {
        const next = { ...prev };
        delete next[product.id];
        return next;
      }
      return {
        ...prev,
        [product.id]: {
          checked: true,
          returnedPieces: current?.returnedPieces || '1',
          product: current?.product || product,
        },
      };
    });
    if (totalManuallyEdited) {
      setTotalManuallyEdited(false);
      setTotalOverride('');
    }
  };

  const handleReturnedPiecesChange = (product, value) => {
    if (value !== '' && (!/^\d+$/.test(value) || parseInt(value, 10) < 0)) return;
    setSelection((prev) => ({
      ...prev,
      [product.id]: {
        checked: true,
        returnedPieces: value,
        product: prev[product.id]?.product || product,
      },
    }));
    if (totalManuallyEdited) {
      setTotalManuallyEdited(false);
      setTotalOverride('');
    }
  };

  const handleTotalChange = (e) => {
    setTotalOverride(e.target.value);
    setTotalManuallyEdited(true);
  };

  const handleUseCalculatedTotal = () => {
    setTotalManuallyEdited(false);
    setTotalOverride('');
  };

  const handleSubmit = () => {
    const items = lineItems
      .filter((item) => item.quantity > 0)
      .map((item) => buildReturnItemPayload(item.product, item.quantity));

    if (items.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one product with returned pieces',
        severity: 'error',
      });
      return;
    }

    createReturnInvoice.mutate(
      {
        salesId: saleId,
        items,
        totalAmount: calculatedTotal,
        totalReturnAmount: displayTotal,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: 'Return invoice saved successfully',
            severity: 'success',
          });
          setSelection({});
          setNotes('');
          setTotalManuallyEdited(false);
          setTotalOverride('');
          refetchInvoices();
        },
        onError: (error) => {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || 'Failed to save return invoice',
            severity: 'error',
          });
        },
      }
    );
  };

  const handleDeleteInvoice = (id) => {
    if (!window.confirm('Delete this return invoice?')) return;
    deleteReturnInvoice.mutate(id, {
      onSuccess: () => {
        setSnackbar({ open: true, message: 'Return invoice deleted', severity: 'success' });
        refetchInvoices();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || 'Failed to delete return invoice',
          severity: 'error',
        });
      },
    });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  const saleLabel = sale
    ? `Sales rep: ${sale.user?.fullName || sale.code} (${sale.code})`
    : '';

  const handlePrintInvoice = async (invoiceId) => {
    try {
      setPrintingId(invoiceId);
      const fullInvoice = await fetchReturnInvoiceById(invoiceId);
      printReturnInvoices([fullInvoice], saleLabel);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to print',
        severity: 'error',
      });
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/sales')} aria-label="Back to sales">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                Return Invoice
              </Typography>
              {sale && (
                <Typography variant="body2" color="text.secondary">
                  Sales rep: {sale.user?.fullName || sale.code} ({sale.code})
                </Typography>
              )}
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<OrdersIcon />}
            onClick={() => navigate(`/sales/${saleId}/orders`)}
          >
            Sales rep orders
          </Button>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Existing return invoices
            </Typography>
            {invoicesLoading ? (
              <LinearProgress />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell align="right">Total amount</TableCell>
                        <TableCell align="right">Return amount</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No return invoices yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {String(invoice.id).slice(0, 8)}…
                            </TableCell>
                            <TableCell align="right">
                              ${parseFloat(invoice.totalAmount ?? 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              ${parseFloat(invoice.totalReturnAmount ?? 0).toFixed(2)}
                            </TableCell>
                            <TableCell>{invoice.notes || '—'}</TableCell>
                            <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  navigate(`/sales/${saleId}/return-invoice/${invoice.id}`)
                                }
                                title="View returned items"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handlePrintInvoice(invoice.id)}
                                disabled={printingId === invoice.id}
                                title="Print"
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteInvoice(invoice.id)}
                                disabled={deleteReturnInvoice.isLoading}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25]}
                  count={invoiceMeta.total ?? 0}
                  rowsPerPage={invoiceRowsPerPage}
                  page={invoicePage}
                  onPageChange={(_, newPage) => setInvoicePage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setInvoiceRowsPerPage(parseInt(e.target.value, 10));
                    setInvoicePage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          New return invoice
        </Typography>
        {selectedCount > 0 && (
          <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
            {selectedCount} product(s) selected across pages
          </Typography>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <TextField
                fullWidth
                size="small"
                label="Search product (name or Arabic name)"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setProductPage(0);
                }}
                placeholder="Search by English or Arabic name"
              />
            </Box>
            {productsLoading && !productData ? (
              <LinearProgress />
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Select</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Arabic Name</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right" sx={{ minWidth: 160 }}>
                        Returned Pieces
                      </TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayProducts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {productSearch.trim()
                            ? 'No products match your search'
                            : 'No products found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayProducts.map((product) => {
                        const isSelected = !!selection[product.id]?.checked;
                        const returnedPieces = selection[product.id]?.returnedPieces ?? '';
                        const unitPrice = parseFloat(product.price) || 0;
                        const lineTotal = isSelected
                          ? unitPrice * (parseInt(returnedPieces, 10) || 0)
                          : 0;

                        return (
                          <TableRow key={product.id} selected={isSelected} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleProduct(product)}
                              />
                            </TableCell>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.nameArabic}</TableCell>
                            <TableCell align="right">${unitPrice.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={returnedPieces}
                                onChange={(e) =>
                                  handleReturnedPiecesChange(product, e.target.value)
                                }
                                disabled={!isSelected}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {isSelected ? `$${lineTotal.toFixed(2)}` : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={productTableCount}
                  rowsPerPage={productRowsPerPage}
                  page={productPage}
                  onPageChange={(_, newPage) => setProductPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setProductRowsPerPage(parseInt(e.target.value, 10));
                    setProductPage(0);
                  }}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Box
              display="flex"
              flexWrap="wrap"
              gap={3}
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Calculated total (from selected items)
                </Typography>
                <Typography variant="h6">${calculatedTotal.toFixed(2)}</Typography>
                {totalManuallyEdited && (
                  <Button size="small" onClick={handleUseCalculatedTotal} sx={{ mt: 0.5 }}>
                    Use calculated total
                  </Button>
                )}
              </Box>
              <TextField
                label="Total return amount (editable)"
                type="number"
                value={totalManuallyEdited ? totalOverride : calculatedTotal.toFixed(2)}
                onChange={handleTotalChange}
                onFocus={() => {
                  if (!totalManuallyEdited) {
                    setTotalOverride(calculatedTotal.toFixed(2));
                    setTotalManuallyEdited(true);
                  }
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ minWidth: 220 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={createReturnInvoice.isLoading || selectedCount === 0}
              >
                {createReturnInvoice.isLoading ? 'Saving...' : 'Save Return Invoice'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Total amount: <strong>${calculatedTotal.toFixed(2)}</strong>
              {' · '}
              Return amount: <strong>${displayTotal.toFixed(2)}</strong>
              {totalManuallyEdited && ' (manually adjusted)'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReturnInvoicePage;
